/**
 * Portable voice I/O contracts — zero platform imports. The engine, STT and TTS clients in this
 * directory run anywhere with a `WebSocket` global (Bun, Node, browser); platform backends
 * (mic capture, audio playback) plug in through these seams.
 */

export type VoiceState = 'idle' | 'listening' | 'thinking' | 'speaking';

/** STT input sample format — sources MUST deliver this (downsample/convert in the adapter). */
export const STT_SAMPLE_RATE = 16000; // Hz, mono, s16le
/** Playback format the TTS requests and sinks must play. */
export const TTS_SAMPLE_RATE = 44100; // Hz, mono, s16le

/** A microphone (or any PCM) source. Emits s16le mono 16kHz chunks.
 *  Node: mic-aec/ffmpeg child process. Web: getUserMedia({ echoCancellation: true }) + worklet. */
export interface AudioSource {
  /** `aec` = the source is echo-cancelled (assistant's own TTS never reaches the chunks) —
   *  selects the engine's trivial barge-in path vs the heuristic echo tier. */
  readonly aec: boolean;
  start(onChunk: (pcm: Uint8Array) => void): void | Promise<void>;
  stop(): void;
  /** Unrecoverable source failure (e.g. mic permission denied → engine produces no audio AND, in the
   *  duplex AEC engine, no playback). The host should tear voice down rather than spin reconnects. */
  onFatal?: (message: string) => void;
}

/** An audio playback sink for s16le mono 44.1kHz PCM.
 *  Node: per-turn ffplay. Web: AudioContext/worklet ring buffer. */
export interface AudioSink {
  /** start a new spoken turn (may recycle or recreate the underlying player) */
  markTurn(): void;
  write(pcm: Uint8Array): void;
  /** estimated ms until queued audio finishes playing */
  drainMs(): number;
  /** ms of audio actually played this turn */
  playedMs(): number;
  /** stop playback NOW (barge-in primitive) */
  kill(): void;
  /** optional exact-sample pause/resume — enables the overlap trail-off tier (web: AudioContext
   *  suspend/resume; CLI AEC helper: control frames). Sinks without it degrade to interrupt-only
   *  turn-taking. Nothing is lost across a pause; playedMs/drainMs must exclude paused time. */
  pause?(): void;
  resume?(): void;
}

/** Static key (server/CLI) or an async getter (browser: fetch a short-lived token from YOUR
 *  backend). Getters are invoked on EVERY (re)connect — temp tokens expire, so a reconnect
 *  must re-mint, never reuse the boot-time token. */
export type AuthProvider = string | (() => string | Promise<string>);

export async function resolveAuth(auth: AuthProvider): Promise<string> {
  return typeof auth === 'function' ? await auth() : auth;
}
